import { useEffect, useState, type ReactElement } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useLocation } from "react-router-dom";
import taskerAuth from "../../../services/taskerAuth";

type Props = {
  children: ReactElement;
};

const HEARTBEAT_MS = 5 * 60_000;

type Status = "checking" | "authed" | "expired";

function VendorAuthGate({ children }: Props) {
  const location = useLocation();
  const [status, setStatus] = useState<Status>(() =>
    taskerAuth.getTaskerToken() ? "checking" : "expired",
  );

  // Initial mount check: verify the stored token still refreshes cleanly.
  useEffect(() => {
    if (status !== "checking") return;
    let cancelled = false;
    (async () => {
      const fresh = await taskerAuth.ensureVendorToken();
      if (cancelled) return;
      setStatus(fresh ? "authed" : "expired");
    })();
    return () => {
      cancelled = true;
    };
  }, [status]);

  // Heartbeat runs only while authed — separate effect so it doesn't fire
  // alongside the mount check and doesn't restart every render.
  useEffect(() => {
    if (status !== "authed") return;
    let cancelled = false;
    const id = setInterval(async () => {
      const fresh = await taskerAuth.ensureVendorToken();
      if (cancelled) return;
      if (!fresh) setStatus("expired");
    }, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [status]);

  if (status === "expired") {
    if (taskerAuth.getTaskerToken()) taskerAuth.clearTaskerSession();
    return (
      <Navigate
        to={`/vendor-onboarding/login${location.search}`}
        replace
      />
    );
  }

  if (status === "checking") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return children;
}

export default VendorAuthGate;
